'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import Link from 'next/link'
import { scroller } from 'react-scroll'
import { useAuthStore } from '@/lib/stores/authStore'

const scrollToSection = (id: string) => {
  scroller.scrollTo(id, {
    duration: 300,
    smooth: 'easeInOutQuart',
    offset: -64,
  })
}

const CustomHeader = ({ basePath }: { basePath: string }) => {
  const router = useRouter()
  const pathname = usePathname()
  const { user } = useAuthStore()
  const isLoggedIn = !!user
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const mobileMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isMobileMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target as Node)
      ) {
        setIsMobileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMobileMenuOpen])

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [pathname])

  const handleBenefitClick = () => {
    setIsMobileMenuOpen(false)
    if (document.getElementById('benefit')) {
      scrollToSection('benefit')
    } else {
      router.push(`${basePath}#benefit`)
    }
  }

  const handleFaqClick = () => {
    setIsMobileMenuOpen(false)
    if (document.getElementById('faq')) {
      scrollToSection('faq')
    } else {
      router.push(`${basePath}#faq`)
    }
  }

  return (
    <>
      <header className="fixed top-0 z-20 h-[60px] w-full bg-black/40 backdrop-blur-[10px] pc:h-[75px]">
        <div className="mx-auto flex h-full flex-row items-center justify-between px-4 pc:mx-auto pc:max-w-7xl pc:px-6">
          <button className="cursor-pointer h-[24px] pc:h-[40px] w-auto" onClick={() => router.push('/')}>
            <img
              src="https://d35ai18pny966l.cloudfront.net/course/events/ktechpioneers/header_logo_ktechpioneers_white.png"
              className="h-full w-auto object-contain"
              alt="K-TECH PIONEERS"
            />
          </button>

          <div className="hidden pc:flex flex-row items-center justify-center gap-2 pc:gap-4">
            <Link
              href="https://glob-dev-resource.s3.ap-northeast-2.amazonaws.com/course/events/ktechpioneers/K-Tech_Pioneers_Recruitment_Notice_and_Application_Forms.zip"
              target="_blank"
              download
              className="group flex h-[32px] items-center justify-center bg-[#242B36] rounded-full px-4 pc:min-w-[120px] pc:h-[36px] pc:px-3">
              <p className="text-xs text-white transition-colors duration-300 pc:text-sm">
                모집 공고문
              </p>
              <img
                src="https://d35ai18pny966l.cloudfront.net/course/events/ktechpioneers/icon_download.png"
                className="w-5 h-auto object-contain"
                alt="download"
              />
            </Link>
            {!pathname.endsWith('/list') && (
              <Link
                href={`${basePath === '/' ? '' : basePath}/list`}
                className="flex h-[32px] items-center justify-center bg-[#242B36] rounded-full px-4 pc:h-[36px] pc:min-w-[100px] pc:px-3">
                <p className="text-xs text-white transition-colors duration-300 pc:text-sm">
                  협력 과제
                </p>
              </Link>
            )}
            <button
              onClick={handleBenefitClick}
              className="flex h-[32px] items-center justify-center rounded-full bg-[#242B36] min-w-[77px] pc:min-w-[100px] px-4 pc:h-[36px] pc:px-3">
              <p className="text-xs text-white transition-colors duration-300 pc:text-sm">
                참가자 혜택
              </p>
            </button>
            <button
              onClick={handleFaqClick}
              className="flex h-[32px] items-center justify-center rounded-full bg-[#242B36] min-w-[77px] pc:min-w-[100px] px-4 pc:h-[36px] pc:px-3">
              <p className="text-xs text-white transition-colors duration-300 pc:text-sm">
                FAQ
              </p>
            </button>
            {isLoggedIn ? (
              <button
                onClick={() => router.push('/my/application')}
                className="flex h-[32px] items-center justify-center rounded-full bg-[#3385FF] min-w-[77px] pc:min-w-[100px] px-4 pc:h-[36px] pc:px-3">
                <p className="text-xs text-white transition-colors duration-300 pc:text-sm">
                  마이페이지
                </p>
              </button>
            ) : (
              <button
                onClick={() => {
                  const currentUrl = window.location.href
                  router.push(`/login?prev=${encodeURIComponent(currentUrl)}`)
                }}
                className="flex h-[32px] items-center justify-center rounded-full bg-[#3385FF] min-w-[77px] pc:min-w-[90px] px-4 pc:h-[36px] pc:px-3">
                <p className="text-xs text-white transition-colors duration-300 pc:text-sm">
                  Sign in
                </p>
              </button>
            )}
          </div>

          <div className="pc:hidden relative" ref={mobileMenuRef}>
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className="flex h-[36px] w-[36px] items-center justify-center"
              aria-label="menu">
              <img
                src="https://d35ai18pny966l.cloudfront.net/course/events/ktechpioneers/icon_menu.png"
                className="h-6 w-6 object-contain"
                alt="menu"
              />
            </button>
            {isMobileMenuOpen && (
              <div className="absolute right-0 top-[44px] flex w-[180px] flex-col gap-2 rounded-2xl bg-[#1A1F28] p-3 shadow-lg">
                <Link
                  href="https://glob-dev-resource.s3.ap-northeast-2.amazonaws.com/course/events/ktechpioneers/K-Tech_Pioneers_Recruitment_Notice_and_Application_Forms.zip"
                  target="_blank"
                  download
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex h-[36px] items-center justify-center rounded-full bg-[#242B36] gap-1.5 px-4">
                  <p className="text-xs text-white">모집 공고문</p>
                  <img
                    src="https://d35ai18pny966l.cloudfront.net/course/events/ktechpioneers/icon_download.png"
                    className="h-4 w-4 object-contain"
                    alt="download"
                  />
                </Link>
                {!pathname.endsWith('/list') && (
                  <Link
                    href={`${basePath === '/' ? '' : basePath}/list`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex h-[36px] items-center justify-center rounded-full bg-[#242B36] px-4">
                    <p className="text-xs text-white">협력 과제</p>
                  </Link>
                )}
                <button
                  onClick={handleBenefitClick}
                  className="flex h-[36px] items-center justify-center rounded-full bg-[#242B36] px-4">
                  <p className="text-xs text-white">참가자 혜택</p>
                </button>
                <button
                  onClick={handleFaqClick}
                  className="flex h-[36px] items-center justify-center rounded-full bg-[#242B36] px-4">
                  <p className="text-xs text-white">FAQ</p>
                </button>
                {isLoggedIn ? (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      router.push('/my/application')
                    }}
                    className="flex h-[36px] items-center justify-center rounded-full bg-[#3385FF] px-4">
                    <p className="text-xs text-white">마이페이지</p>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      const currentUrl = window.location.href
                      router.push(`/login?prev=${encodeURIComponent(currentUrl)}`)
                    }}
                    className="flex h-[36px] items-center justify-center rounded-full bg-[#3385FF] px-4">
                    <p className="text-xs text-white">Sign in</p>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  )
}

export default CustomHeader
